import { useCallback, useState, useEffect } from 'react';
import { View, StyleSheet } from 'react-native';
import { useNavigate } from 'react-router';
import { theme, Text, Button, FormInput } from '@socrait/react';
import { CredentialResponse, GoogleLogin, useGoogleOAuth } from '@react-oauth/google';

import { HyperLink } from '~/components/HyperLink';
import { Loading } from '~/components/Loading';
import { OrDivider } from '~/components/OrDivider';
import { useLogInMutation } from '~/services/useLoginMutation';
import { useEmailPasswordLoginMutation } from '~/services/useEmailPasswordLoginMutation';
import { showError } from '~/utils/showError';
import { isPydanticValidationError, parsePydanticErrors } from '~/utils/pydanticErrors';
import { useEnvironmentVariables } from '~/hooks/useEnvironmentVariables';
import { PublicPageLayout } from '~/layouts/PublicPageLayout';
import { SplashPanel, usePhaseAnimation } from '~/components/SplashPanel';
import { AuthenticationError, ClientError } from '~/errors';

export function LoginPage() {
  const [scriptLoading, setScriptLoading] = useState(true);
  const [scriptError, setScriptError] = useState(false);
  const [isLoginPending, setIsLoginPending] = useState(false);
  const [isEmailPasswordLoginPending, setIsEmailPasswordLoginPending] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const { phase, fadeAnim } = usePhaseAnimation();

  const { mutate } = useLogInMutation();
  const { mutate: emailPasswordMutate } = useEmailPasswordLoginMutation();
  const { scriptLoadedSuccessfully } = useGoogleOAuth();
  const navigate = useNavigate();
  const { isMockMode } = useEnvironmentVariables();
  const emailValid = email.trim().length > 0 && email.includes('@');
  const hasContent = email.trim().length > 0 && password.trim().length > 0;

  useEffect(() => {
    setScriptLoading(true);

    const timeoutId = setTimeout(() => {
      if (!scriptLoadedSuccessfully) {
        setScriptError(true);
        setScriptLoading(false);
      }
    }, 10000);

    if (scriptLoadedSuccessfully) {
      setScriptLoading(false);
      setScriptError(false);
      clearTimeout(timeoutId);
    }

    return () => clearTimeout(timeoutId);
  }, [scriptLoadedSuccessfully]);

  const handleGoogleSso = useCallback(
    (credentialResponse: CredentialResponse) => {
      setIsLoginPending(true);
      mutate(
        { idToken: credentialResponse.credential! },
        {
          onSuccess: () => navigate('/', { replace: true }),
          onError: (error) => {
            setIsLoginPending(false);
            showError(error, 'Login failed. Please try again, or contact support if the issue persists.');
          },
        },
      );
    },
    [mutate, navigate],
  );

  const handleGoogleSsoError = useCallback(() => {
    setIsLoginPending(false);
    showError(new Error(), 'Login failed. Please try again, or contact support if the issue persists.');
  }, []);

  const handleEmailPasswordLogin = useCallback(() => {
    if (!emailValid) {
      showError(new Error(), 'Please enter a valid email address.');
      return;
    }

    setIsEmailPasswordLoginPending(true);
    emailPasswordMutate(
      { email: email.trim(), password },
      {
        onSuccess: () => navigate('/', { replace: true }),
        onError: (error) => {
          setIsEmailPasswordLoginPending(false);

          if (error instanceof AuthenticationError && error.statusCode === 401) {
            showError(error, 'Invalid email or password.');
          } else if (
            error instanceof AuthenticationError &&
            error.statusCode === 403 &&
            error.errorCode === 'email_not_verified'
          ) {
            navigate('/verify', { state: { email: email.trim() } });
          } else if (
            error instanceof AuthenticationError &&
            error.statusCode === 403 &&
            (error.errorCode === 'email_password_auth_disabled' || error.errorCode === 'test_account_auth_disabled')
          ) {
            showError(error, 'This login method is currently unavailable.');
          } else if (
            error instanceof ClientError &&
            error.statusCode === 422 &&
            isPydanticValidationError(error.detail)
          ) {
            const { fieldErrors, nonFieldErrors } = parsePydanticErrors(error.detail);
            setEmailError(fieldErrors['email'] ?? '');
            setPasswordError(fieldErrors['password'] ?? '');
            if (nonFieldErrors.length > 0) {
              showError(error, nonFieldErrors[0]);
            } else if (Object.keys(fieldErrors).length === 0) {
              showError(error, 'Please check your input and try again.');
            }
          } else {
            showError(error, 'Login failed. Please try again, or contact support if the issue persists.');
          }
        },
      },
    );
  }, [email, password, emailValid, emailPasswordMutate, navigate]);

  const retryScriptLoading = useCallback(() => {
    setScriptError(false);
    setScriptLoading(true);
    window.location.reload();
  }, []);

  if ((!isMockMode && scriptLoading) || isLoginPending) {
    return <Loading />;
  }

  if (scriptError) {
    return (
      <View style={styles.errorContainer}>
        <Text style={styles.errorText}>Failed to load Google authentication services.</Text>
        <Button name="Retry" onPress={retryScriptLoading} containerStyle={styles.retryButton} />
      </View>
    );
  }

  const canSubmit = hasContent && !isEmailPasswordLoginPending;

  return (
    <PublicPageLayout>
      <View style={styles.content}>
        <SplashPanel phase={phase} fadeAnim={fadeAnim} />
        <View style={styles.formWrapper}>
          <View style={styles.formContainer}>
            <View style={styles.form}>
              {isMockMode ? (
                <View style={styles.mockButtons}>
                  <Button
                    name="Mock Sign In"
                    variant="primary"
                    onPress={() => handleGoogleSso({ credential: 'mock-id-token' } as CredentialResponse)}
                  />
                  <Button
                    name="Mock Set Up"
                    variant="secondary"
                    onPress={() => handleGoogleSso({ credential: 'mock-setup' } as CredentialResponse)}
                  />
                </View>
              ) : (
                <GoogleLogin
                  onSuccess={handleGoogleSso}
                  onError={handleGoogleSsoError}
                  text="continue_with"
                  size="large"
                  shape="circle"
                  theme="outline"
                  logo_alignment="left"
                  width="304px"
                />
              )}
            </View>
            <OrDivider />
            <View style={styles.form}>
              <View style={styles.inputGroup}>
                <FormInput
                  placeholderText="Email"
                  value={email}
                  onChangeText={(text) => {
                    setEmail(text);
                    setEmailError('');
                  }}
                  onSubmit={() => canSubmit && handleEmailPasswordLogin()}
                  hasError={emailError.length > 0}
                  errorMessage={emailError}
                  containerStyle={styles.inputContainer}
                  inputStyle={styles.roundedInput}
                  formContainerStyle={styles.cardFormContainer}
                />
                <FormInput
                  placeholderText="Password"
                  value={password}
                  onChangeText={(text) => {
                    setPassword(text);
                    setPasswordError('');
                  }}
                  onSubmit={() => canSubmit && handleEmailPasswordLogin()}
                  hasError={passwordError.length > 0}
                  errorMessage={passwordError}
                  shadow
                  containerStyle={styles.inputContainer}
                  inputStyle={styles.roundedInput}
                  formContainerStyle={styles.cardFormContainer}
                />
              </View>
              <Button
                name="Log in"
                variant="primary"
                disabled={!canSubmit}
                loading={isEmailPasswordLoginPending}
                onPress={handleEmailPasswordLogin}
                containerStyle={styles.submitButton}
              />
            </View>
            <Text style={styles.signupPrompt}>
              {"Don't have an account? "}
              <HyperLink onPress={() => navigate('/signup')} variant="primary" style={styles.signupPromptLink}>
                Sign up
              </HyperLink>
            </Text>
          </View>
        </View>
      </View>
    </PublicPageLayout>
  );
}

const styles = StyleSheet.create({
  content: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: theme.spacing.xlarge,
    paddingVertical: theme.spacing.xlarge * 2,
    gap: theme.spacing.xlarge,
  },
  formWrapper: {
    display: 'flex',
    width: 304,
    flexDirection: 'column',
    alignItems: 'center',
  },
  formContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing.xlarge,
    alignSelf: 'stretch',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing.xxlarge,
    alignSelf: 'stretch',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    alignSelf: 'stretch',
    gap: theme.spacing.medium,
  },
  mockButtons: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing.medium,
  },
  inputContainer: {
    alignSelf: 'stretch',
  },
  cardFormContainer: {
    height: 48,
    width: '100%',
  },
  roundedInput: {
    borderRadius: theme.borderRadius.large,
    height: 48,
  },
  submitButton: {
    alignSelf: 'stretch',
  },
  signupPrompt: {
    fontSize: theme.fontSizes.xsmall,
    color: theme.colors.darkGray,
  },
  signupPromptLink: {
    fontSize: theme.fontSizes.xsmall,
    textDecorationLine: 'underline',
  },
  errorText: {
    color: theme.colors.red,
    fontFamily: theme.fonts.regular,
    fontSize: theme.fontSizes.small,
    textAlign: 'center',
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.xlarge,
    height: '100%',
    padding: theme.spacing.xlarge,
  },
  retryButton: {
    marginTop: theme.spacing.medium,
  },
});
